import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { usePlaylist } from '@/hooks/usePlaylist';
import { useVolumeNormalization } from '@/hooks/useVolumeNormalization';
import { useToast } from '@/components/ui/use-toast';
import { bufferOptimizer } from '@/utils/bufferOptimizer';

const AudioContext = createContext();

export function useAudioContext() {
  return useContext(AudioContext);
}

export function AudioProvider({ children }) {
  const { toast } = useToast();
  
  const audioRef = useRef(new Audio()); 
  const analyserRef = useRef(null); 
  const dataArrayRef = useRef(null); 

  const playerHook = useAudioPlayer(audioRef, analyserRef, dataArrayRef);
  const playlistHook = usePlaylist();

  const { 
    currentSong: playerLoadedSongFromHook,
    isPlaying, 
    currentTime, 
    duration: playerDuration, 
    volume, 
    isLooping: isSongLooping, 
    play: audioPlayerPlay, 
    pause: audioPlayerPause, 
    seek, 
    setVolume: setPlayerVolume, 
    toggleLoop: toggleSongLoop, 
    setIsPlaylistLooping: setPlayerIsPlaylistLooping, 
    loadSong: loadAudioFileIntoPlayer 
  } = playerHook;

  const { 
    playlist, 
    currentIndex, 
    setPlaylist, 
    setCurrentIndex, 
    addSongs: addFilesToPlaylistHook, 
    removeSong, 
    reorderSongs, 
    savePlaylist, 
    loadPlaylist: loadPlaylistFromSaved, 
    deleteSavedPlaylist,
    savedPlaylists,
    clearPlaylist
  } = playlistHook;

  const currentSongForUI = playlist[currentIndex] || null; 

  const [isInternalPlaylistLooping, setIsInternalPlaylistLooping] = useState(false);
  const [totalPlaylistDurationState, setTotalPlaylistDurationState] = useState(0);

  useEffect(() => {
    setPlayerIsPlaylistLooping(isInternalPlaylistLooping);
  }, [isInternalPlaylistLooping, setPlayerIsPlaylistLooping]);

  const { 
    isNormalizationEnabled, 
    toggleNormalization, 
    normalizedVolume 
  } = useVolumeNormalization(currentSongForUI, volume, setPlayerVolume, toast);

  const [isPlayerMinimized, setIsPlayerMinimized] = useState(false);
  const [showPlaylistManager, setShowPlaylistManager] = useState(false);
  const [visualizerType, setVisualizerType] = useState('cosmic');
  const [isPlaylistCollapsed, setIsPlaylistCollapsed] = useState(true);
  const [isStreamerMode, setIsStreamerMode] = useState(false);
  const [lyricsPosition, setLyricsPosition] = useState('top');
  
  const [playAfterLoad, setPlayAfterLoad] = useState(false);
  const songEndHandledRef = useRef(false); 
  const playNextInProgressRef = useRef(false); 

  const toggleLyricsPosition = useCallback(() => {
    setLyricsPosition(prev => (prev === 'top' ? 'bottom' : 'top'));
  }, []);

  const calculateAndSetTotalDuration = useCallback((currentPlaylist) => {
    if (!currentPlaylist) {
      console.log("AudioContext: calculateAndSetTotalDuration called with undefined playlist");
      return;
    }
    const newTotalDuration = currentPlaylist.reduce((acc, song) => acc + (song.duration || 0), 0);
    
    console.log("AudioContext: Calculating Total Duration");
    console.log("AudioContext: Current Playlist:", currentPlaylist.map(s => ({ name: s.name, duration: s.duration })));
    console.log("AudioContext: New Total Duration:", newTotalDuration);

    setTotalPlaylistDurationState(newTotalDuration);
  }, []);

  useEffect(() => {
    calculateAndSetTotalDuration(playlist);
  }, [playlist, playlist.map(song => song.duration).join(','), calculateAndSetTotalDuration]); 


  useEffect(() => {
    const loadAndPotentiallyPlay = async () => {
      if (currentSongForUI && currentSongForUI.file) {
        if (!playerLoadedSongFromHook || playerLoadedSongFromHook.id !== currentSongForUI.id) {
          songEndHandledRef.current = false;
          const loadedSuccessfully = await loadAudioFileIntoPlayer(currentSongForUI); 
          if (loadedSuccessfully) {
            if (playAfterLoad) {
              await audioPlayerPlay(); 
              setPlayAfterLoad(false); 
            }
          } else {
            setPlayAfterLoad(false);
          }
        } else if (playAfterLoad && playerLoadedSongFromHook && playerLoadedSongFromHook.id === currentSongForUI.id) {
          songEndHandledRef.current = false;
          await audioPlayerPlay();
          setPlayAfterLoad(false);
        }
      } else if (!currentSongForUI && playerLoadedSongFromHook) { 
        songEndHandledRef.current = false;
        await loadAudioFileIntoPlayer(null); 
        setPlayAfterLoad(false);
      }
    };
    loadAndPotentiallyPlay();
  }, [currentSongForUI, playerLoadedSongFromHook, loadAudioFileIntoPlayer, audioPlayerPlay, playAfterLoad]);

  // Update buffer optimizer with playlist data when playlist or current index changes
  useEffect(() => {
    if (playlist.length > 0 && bufferOptimizer.isOptimizing) {
      // Find the next few tracks for preloading
      const upcomingTracks = [];
      for (let i = 1; i <= 3; i++) { // Get next 3 tracks
        const nextIndex = (currentIndex + i) % playlist.length;
        if (nextIndex !== currentIndex && playlist[nextIndex]) {
          upcomingTracks.push(playlist[nextIndex]);
        }
      }
      
      // Update the buffer optimizer with current playlist context
      if (audioRef.current && currentSongForUI) {
        bufferOptimizer.stopBufferOptimization();
        bufferOptimizer.initializeBufferOptimization(audioRef.current, [currentSongForUI, ...upcomingTracks]);
      }
    }
  }, [playlist, currentIndex, currentSongForUI]);


  const executePlayPause = useCallback(async () => { 
    const songToOperateOn = playerLoadedSongFromHook || currentSongForUI;

    if (!songToOperateOn || !songToOperateOn.file) { 
      toast({ title: "🤔 No Song Loaded", description: "Please select a song from the playlist.", variant: "destructive" });
      return;
    }
    
    if (!playerLoadedSongFromHook || playerLoadedSongFromHook.id !== songToOperateOn.id) {
        setCurrentIndex(playlist.findIndex(s => s.id === songToOperateOn.id)); 
        songEndHandledRef.current = false; 
        setPlayAfterLoad(true); 
        return;
    }

    if (isPlaying) {
      audioPlayerPause();
    } else {
      songEndHandledRef.current = false; 
      await audioPlayerPlay();
    }
  }, [playerLoadedSongFromHook, currentSongForUI, isPlaying, audioPlayerPlay, audioPlayerPause, toast, setCurrentIndex, playlist]);

  const forcePlay = useCallback(async () => {
    const songToOperateOn = playerLoadedSongFromHook || currentSongForUI;

    if (!songToOperateOn || !songToOperateOn.file) { 
      toast({ title: "🤔 No Song Loaded", description: "Please select a song from the playlist.", variant: "destructive" });
      return;
    }
    
    if (!playerLoadedSongFromHook || playerLoadedSongFromHook.id !== songToOperateOn.id) {
        setCurrentIndex(playlist.findIndex(s => s.id === songToOperateOn.id)); 
        songEndHandledRef.current = false; 
        setPlayAfterLoad(true); 
        return;
    }

    songEndHandledRef.current = false; 
    await audioPlayerPlay();
  }, [playerLoadedSongFromHook, currentSongForUI, audioPlayerPlay, toast, setCurrentIndex, playlist]);

  const forcePause = useCallback(() => {
    audioPlayerPause();
  }, [audioPlayerPause]);
  
  const playSongAtIndex = useCallback((index) => {
    if (index >= 0 && index < playlist.length) {
      const songToPlay = playlist[index];
      if (songToPlay && songToPlay.file) { 
        songEndHandledRef.current = false;
        setCurrentIndex(index); 
        setPlayAfterLoad(true); 
      } else {
         toast({ title: "File Missing", description: `Song "${songToPlay?.name || 'selected song'}" is missing its file. Please re-add it.`, variant: "destructive" });
      }
    }
  }, [playlist, setCurrentIndex, toast]);

  const findNextPlayableSong = useCallback((startIdx, direction = 1) => {
    console.log(`🔍 findNextPlayableSong called: startIdx=${startIdx}, direction=${direction}, playlist.length=${playlist.length}, looping=${isInternalPlaylistLooping}`);
    
    if (playlist.length === 0) return -1;
    
    for (let i = 0; i < playlist.length; i++) {
      // Calculate next index
      let nextIdx = startIdx + (direction * (i + 1));
      console.log(`🔍 Iteration ${i}: calculated nextIdx = ${startIdx} + (${direction} * ${i + 1}) = ${nextIdx}`);
      
      // Handle wrap-around based on playlist looping setting
      if (!isInternalPlaylistLooping) {
        // No looping - check bounds
        if (nextIdx < 0 || nextIdx >= playlist.length) {
          console.log(`🔍 No looping: nextIdx ${nextIdx} is out of bounds, breaking`);
          break; // Exit if we've gone beyond the playlist bounds
        }
      } else {
        // With looping - wrap around
        const originalNextIdx = nextIdx;
        nextIdx = ((nextIdx % playlist.length) + playlist.length) % playlist.length;
        console.log(`🔍 With looping: wrapped ${originalNextIdx} → ${nextIdx}`);
      }
      
      console.log(`🔍 Checking song at index ${nextIdx}: hasFile=${!!(playlist[nextIdx] && playlist[nextIdx].file)}`);
      
      if (playlist[nextIdx] && playlist[nextIdx].file) {
        console.log(`🔍 Found playable song at index ${nextIdx}, returning`);
        return nextIdx;
      }
    }
    
    console.log(`🔍 No playable songs found, returning -1`);
    // If no playable songs found
    return -1; 
  }, [playlist, isInternalPlaylistLooping]);

  const playNext = useCallback((shouldAutoPlay = true) => {
    console.log(`🎵 playNext called: currentIndex=${currentIndex}, shouldAutoPlay=${shouldAutoPlay}`);
    
    // Add protection against rapid successive calls during auto-play
    if (playNextInProgressRef.current && shouldAutoPlay) {
      console.log(`🎵 playNext: Already in progress, ignoring call`);
      return;
    }
    
    if (shouldAutoPlay) {
      playNextInProgressRef.current = true;
    }
    
    const nextPlayableIndex = findNextPlayableSong(currentIndex, 1);
    console.log(`🎵 playNext: findNextPlayableSong returned ${nextPlayableIndex}`);
    
    if (nextPlayableIndex !== -1) {
      songEndHandledRef.current = false;
      console.log(`🎵 playNext: setting currentIndex to ${nextPlayableIndex}`);
      setCurrentIndex(nextPlayableIndex);
      if (shouldAutoPlay) {
        setPlayAfterLoad(true);
        // Reset the flag after a short delay to allow the index change to process
        setTimeout(() => {
          playNextInProgressRef.current = false;
        }, 100);
      }
    } else {
      toast({ title: "No More Playable Songs", variant: "default" });
      if(isPlaying) audioPlayerPause();
      if (shouldAutoPlay) {
        playNextInProgressRef.current = false;
      }
    }
  }, [currentIndex, findNextPlayableSong, setCurrentIndex, toast, isPlaying, audioPlayerPause, isInternalPlaylistLooping]);

  const playPrevious = useCallback(() => {
    const prevPlayableIndex = findNextPlayableSong(currentIndex, -1);
    if (prevPlayableIndex !== -1) {
      songEndHandledRef.current = false;
      setCurrentIndex(prevPlayableIndex);
      setPlayAfterLoad(isPlaying); 
    } else {
      toast({ title: "No More Playable Songs", variant: "default" });
      if(isPlaying) audioPlayerPause();
    }
  }, [currentIndex, findNextPlayableSong, setCurrentIndex, toast, isPlaying, audioPlayerPause]);

  const handleFileUpload = useCallback(async (fileList) => { 
    if (!fileList || fileList.length === 0) return;
    
    const filesArray = Array.from(fileList); 
    const newPlaylist = await addFilesToPlaylistHook(filesArray); 
    
    if (newPlaylist) {
      console.log("AudioContext: handleFileUpload - newPlaylist from hook:", newPlaylist.map(s => ({ name: s.name, duration: s.duration })));
      calculateAndSetTotalDuration(newPlaylist);
    } else {
      console.log("AudioContext: handleFileUpload - newPlaylist from hook was undefined, using playlistHook.playlist");
      calculateAndSetTotalDuration(playlistHook.playlist); 
    }
    
    if (filesArray.length > 0) {
        toast({ title: "🎶 Songs Added", description: `${filesArray.length} song(s) added to playlist.`, duration: 3000 });
    }
  }, [addFilesToPlaylistHook, toast, calculateAndSetTotalDuration, playlistHook.playlist]);

  const togglePlaylist = useCallback(() => setIsPlaylistCollapsed(prev => !prev), []);
  
  const toggleActualPlaylistLoop = useCallback(() => {
    setIsInternalPlaylistLooping(prev => !prev);
  }, []);

  useEffect(() => {
    if (!isPlaying && currentTime > 0 && playerDuration > 0 && Math.abs(currentTime - playerDuration) < 0.5 && playerLoadedSongFromHook?.url && !songEndHandledRef.current) {
      songEndHandledRef.current = true; 
      
      if (isSongLooping) { 
      } else if (isInternalPlaylistLooping) {
        playNext(true); 
      } else if (currentIndex < playlist.length - 1) { 
        playNext(true);
      } else {
      }
    }
  }, [
    isPlaying, currentTime, playerDuration, playerLoadedSongFromHook, 
    isInternalPlaylistLooping, isSongLooping, 
    currentIndex, playlist.length, 
    playNext
  ]);

  useEffect(() => {
    if (audioRef.current) {
      console.log('[AUDIO] Master volume changed:', volume, 'Audio element:', audioRef.current);
      console.log('[AUDIO] Audio element state:', {
        src: audioRef.current.src,
        currentTime: audioRef.current.currentTime,
        paused: audioRef.current.paused,
        ended: audioRef.current.ended,
        readyState: audioRef.current.readyState,
        networkState: audioRef.current.networkState,
        error: audioRef.current.error,
        volume: audioRef.current.volume,
        muted: audioRef.current.muted,
      });
    }
  }, [volume]);

  const contextValue = {
    audioRef, analyserRef, dataArrayRef, 
    
    currentSong: playerLoadedSongFromHook, 
    isPlaying,
    currentTime, 
    duration: playerDuration, 
    volume: isNormalizationEnabled ? normalizedVolume : volume,
    isLooping: isSongLooping, 
    isPlaylistLooping: isInternalPlaylistLooping, 
    
    play: executePlayPause, 
    pause: executePlayPause, 
    handlePlayPause: executePlayPause,
    forcePlay,
    forcePause, 
    seek,
    setVolume: setPlayerVolume, 
    toggleLoop: toggleSongLoop, 
    togglePlaylistLoop: toggleActualPlaylistLoop, 
    
    playlist, 
    currentIndex, 
    setCurrentIndex, 
    addSongs: handleFileUpload, 
    removeSong, 
    reorderSongs, 
    playNext, 
    playPrevious, 
    playSongAtIndex,
    savePlaylist, 
    loadPlaylist: loadPlaylistFromSaved, 
    deleteSavedPlaylist,
    savedPlaylists,
    clearPlaylist, 
    
    isPlayerMinimized, setIsPlayerMinimized,
    showPlaylistManager, setShowPlaylistManager,
    visualizerType, setVisualizerType,
    isPlaylistCollapsed, setIsPlaylistCollapsed,
    togglePlaylist,
    isStreamerMode, setIsStreamerMode,
    lyricsPosition, toggleLyricsPosition,
    
    isNormalizationEnabled, toggleNormalization,
    normalizationApplied: isNormalizationEnabled && playerLoadedSongFromHook ? normalizedVolume : null,
    showNormalizationIndicator: isNormalizationEnabled && playerLoadedSongFromHook?.url && playerDuration > 0,

    totalPlaylistDuration: totalPlaylistDurationState, 
  };

  return <AudioContext.Provider value={contextValue}>{children}</AudioContext.Provider>;
}